import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast, Spinner } from "../../components/ui";
import { FaPlus, FaCheckCircle, FaLock } from "react-icons/fa";
import { Header } from "../../components/modules";
import { FiCreditCard, FiPackage, FiTag, FiCalendar, FiStar, FiSettings, FiSliders } from "react-icons/fi";
import { TbPhotoPlus } from "react-icons/tb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant, { statuses } from "../../constants";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { FaXmark } from "react-icons/fa6";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import {
  dateToUnix,
  unixToDateAndTime,
  resizeImage,
  validate,
} from "../../util";

const inputCls =
  "border-1 w-full disabled:text-gray-400 border-gray-300 py-2 px-3 font-semibold rounded-md placeholder:text-sm text-sm disabled:border-gray-200 disabled:cursor-not-allowed focus:outline-none focus:border-blue transition-colors";
const selectCls =
  "border-1 w-full border-gray-300 py-2 px-3 font-semibold rounded-md placeholder:text-sm text-sm disabled:text-gray-500 disabled:border-gray-200 disabled:cursor-not-allowed focus:outline-none focus:border-blue transition-colors";

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <p className="text-xs font-bold text-gray-700">{label}</p>
    <div className="flex">{children}</div>
  </div>
);

// ─── ConditionsEditor ──────────────────────────────────────────────────────────

const ConditionsEditor = ({ conditions = [], onChange }) => {
  const [newCondition, setNewCondition] = useState("");

  const addCondition = () => {
    const trimmed = newCondition.trim();
    if (!trimmed) return;
    onChange([...conditions, trimmed]);
    setNewCondition("");
  };

  const removeCondition = (index) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  return (
    <div className="col-span-2 border border-gray-200 rounded-md p-3.5 mt-1 bg-gray-50/50">
      <p className="text-xs font-bold text-gray-700 mb-2">เงื่อนไขการใช้คูปอง</p>
      {conditions.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-3">
          {conditions.map((cond, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm shadow-2xs"
            >
              <span className="text-gray-400 font-bold shrink-0 text-xs">
                {idx + 1}.
              </span>
              <p className="flex-1 font-semibold text-gray-800 text-sm">{cond}</p>
              <button
                type="button"
                className="text-red-500 hover:text-red-700 font-bold text-xs"
                onClick={() => removeCondition(idx)}
              >
                ลบ
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          className="border-1 flex-1 border-gray-300 bg-white py-1.5 px-3 font-semibold rounded-md text-sm placeholder:text-sm focus:outline-none focus:border-blue"
          value={newCondition}
          onChange={(e) => setNewCondition(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCondition())}
          placeholder="พิมพ์เงื่อนไขแล้วกด Enter หรือกดปุ่มเพิ่ม"
        />
        <button
          type="button"
          className="shrink-0 bg-blue text-white px-3.5 py-1.5 rounded-md font-bold text-sm hover:opacity-85 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
          onClick={addCondition}
        >
          <FaPlus className="text-xs" /> เพิ่ม
        </button>
      </div>
    </div>
  );
};

// ─── BundleItemRow ─────────────────────────────────────────────────────────────

const BundleItemRow = ({ index, item, allCoupons, onChange, onRemove }) => {
  const handleCouponSelect = (e) => {
    const selected = allCoupons.find((c) => c.id === e.target.value);
    onChange(index, {
      ...item,
      couponId: selected?.id || "",
      couponName: selected?.name || "",
      type: selected?.type || "",
    });
  };

  return (
    <div className="flex gap-2 items-center bg-gray-50/70 p-2 rounded-md border border-gray-200">
      <select
        className={`${selectCls} bg-white flex-1`}
        value={item.couponId}
        onChange={handleCouponSelect}
      >
        <option value="">-- เลือกคูปอง --</option>
        {allCoupons
          .filter((c) => c.expireAt > Date.now() || c.id === item.couponId)
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.type})
            </option>
          ))}
      </select>

      <div className="flex items-center gap-1.5">
        <input
          className={`${inputCls} bg-white w-24 text-center`}
          type="number"
          min="1"
          value={item.quantity}
          onChange={(e) =>
            onChange(index, { ...item, quantity: Number(e.target.value) })
          }
          placeholder="จำนวน"
        />
        <span className="font-bold text-xs text-gray-600 shrink-0">ใบ</span>
      </div>
      <button
        type="button"
        className="p-1 text-gray-400 hover:text-red-600 rounded cursor-pointer transition-colors"
        onClick={() => onRemove(index)}
        title="ลบรายการนี้"
      >
        <FaXmark className="text-base" />
      </button>
    </div>
  );
};

// ─── Categories definition ───────────────────────────────────────────────────

const CATEGORIES = [
  { id: "voucher", name: "คูปองสลาก", icon: <FiTag className="w-4 h-4" /> },
  { id: "discount", name: "คูปองส่วนลด", icon: <FiCreditCard className="w-4 h-4" /> },
  { id: "reserve", name: "คูปองจองสิทธิ์", icon: <FiCalendar className="w-4 h-4" /> },
  { id: "vip", name: "คูปอง VIP", icon: <FiStar className="w-4 h-4" /> },
  { id: "voucher-lock", name: "คูปองล็อคเลข", icon: <FaLock className="w-3.5 h-3.5" /> },
  { id: "bundle", name: "ชุดแพ็กเกจสลาก", icon: <FiPackage className="w-4 h-4" /> },
];

// ─── Main ManageCoupon Component ─────────────────────────────────────────────

export const ManageCoupon = ({ initialCategory = "voucher" }) => {
  const { callApi, updateConfig, configs } = Context();
  const { toast } = useToast();
  const location = useLocation();

  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [coupons, setCoupons] = useState(false);
  const [bundles, setBundles] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedCoupon, setSelectedCoupon] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkCreate, setShowBulkCreate] = useState(false);

  const [selectedBundle, setSelectedBundle] = useState(false);
  const [showCreateBundle, setShowCreateBundle] = useState(false);
  const [updatingConfigKey, setUpdatingConfigKey] = useState(null);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(null);

  // Sync with location.hash or pathname
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && CATEGORIES.some((c) => c.id === hash)) {
      setActiveCategory(hash);
    } else if (location.pathname.includes("/coupon/bundle")) {
      setActiveCategory("bundle");
    }
  }, [location.hash, location.pathname]);

  // Fetch data when active category changes
  useEffect(() => {
    if (activeCategory === "bundle") {
      getBundles();
    } else {
      getCoupons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const getCoupons = async () => {
    setLoading(true);
    const queries = [];
    if (activeCategory === "reserve") {
      queries.push(["type", "reserve"]);
    } else if (activeCategory === "voucher") {
      queries.push(["type", "voucher"]);
    } else if (activeCategory === "discount") {
      queries.push(["type", "discount"]);
    } else if (activeCategory === "vip") {
      queries.push(["vip", true]);
    } else if (activeCategory === "voucher-lock") {
      queries.push(["type", "voucher-lock"]);
    }

    const result = await callApi(
      "coupons-getAllCoupon",
      { queries, limit: 500 },
      { notShowLoading: true },
    );
    setSelectedCoupon(false);
    setCoupons(result || []);
    setLoading(false);
  };

  const getBundles = async () => {
    setLoading(true);
    const result = await callApi(
      "coupons-getAllBundles",
      { limit: 500 },
      { notShowLoading: true },
    );
    setSelectedBundle(false);
    setBundles(result || []);
    setLoading(false);
  };

  const toastSuccess = (msg = "อัพเดตข้อมูลสำเร็จ") =>
    toast({ className: "bg-[rgb(59,176,101)]", title: msg });
  const toastError = (msg = "อัพเดตข้อมูลไม่สำเร็จ") =>
    toast({ className: "bg-[#c15b78]", title: msg });

  const updateGeneralConfig = async (key, value) => {
    if (!window.confirm("ท่านต้องการแก้ไขใช่หรือไม่")) return;
    setUpdatingConfigKey(key);
    const { status } = await callApi(
      "configs-edit",
      { id: "general", key, value },
      { notShowLoading: true },
    );
    status ? toastSuccess() : toastError();
    setUpdatingConfigKey(null);
    updateConfig();
  };

  const handleBulkCouponStatus = async (value) => {
    if (!window.confirm("ท่านต้องการแก้ไขสถานะคูปองทั้งหมดใช่หรือไม่")) return;
    setBulkStatusLoading(value);
    const result = await Promise.allSettled(
      (coupons || []).map((x) =>
        callApi("coupons-editCoupon", { id: x.id, key: "status", value }),
      ),
    );
    result.every((r) => r.value?.status) ? toastSuccess() : toastError();
    setBulkStatusLoading(null);
    getCoupons();
  };

  const handleDeleteVoucherLockAll = async () => {
    if (!window.confirm("ท่านต้องการลบคูปองประเภท voucher-lock ทั้งหมดใช่หรือไม่")) {
      return;
    }
    setLoading(true);
    const voucherLockCoupons = await callApi(
      "coupons-getAllCoupon",
      { queries: [["type", "voucher-lock"]], limit: 1000 },
      { notShowLoading: true },
    );

    if (!voucherLockCoupons || voucherLockCoupons.length === 0) {
      setLoading(false);
      toastError("ไม่พบคูปองประเภท voucher-lock");
      return;
    }

    const result = await Promise.allSettled(
      voucherLockCoupons.map((coupon) =>
        callApi("coupons-deleteCoupon", { id: coupon.id }, { notShowLoading: true }),
      ),
    );

    const successCount = result.filter((r) => r.value?.status).length;
    setLoading(false);
    if (successCount === voucherLockCoupons.length) {
      toastSuccess(`ลบคูปอง voucher-lock สำเร็จ ${successCount} ใบ`);
    } else {
      toastError(`ลบสำเร็จ ${successCount}/${voucherLockCoupons.length} ใบ`);
    }
    getCoupons();
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      <Header
        header="Coupons"
        subHeader="จัดการคูปองและแพ็กเกจ"
        icon={<FiCreditCard className="text-blue" />}
      />

      {/* ─── Config Section (Clean & Simple Original Style) ────────────────── */}
      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FiSettings className="text-blue w-4 h-4" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
            ตั้งค่าระบบคูปองทั่วไป
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            {
              type: "couponVoucher",
              key: "isOpen.couponVoucher",
              name: "คูปองสลาก",
              value: Boolean(configs.isOpen?.couponVoucher),
              closeTimestamp: configs.closeTime?.couponVoucher,
            },
            {
              type: "couponDiscount",
              key: "isOpen.couponDiscount",
              name: "คูปองส่วนลด",
              value: Boolean(configs.isOpen?.couponDiscount),
              closeTimestamp: configs.closeTime?.couponDiscount,
            },
            {
              type: "couponReserve",
              key: "isOpen.couponReserve",
              name: "คูปองจองสิทธิ์",
              value: Boolean(configs.isOpen?.couponReserve),
              closeTimestamp: configs.closeTime?.couponReserve,
            },
            {
              type: "couponVip",
              key: "isOpen.couponVip",
              name: "คูปอง VIP",
              value: Boolean(configs.isOpen?.couponVip),
              closeTimestamp: configs.closeTime?.couponVip,
            },
            {
              type: "couponBundle",
              key: "isOpen.couponBundle",
              name: "ชุดแพ็กเกจ",
              value: Boolean(configs.isOpen?.couponBundle),
              closeTimestamp: configs.closeTime?.couponBundle,
            },
          ].map((item) => {
            const hasCloseTime = Boolean(item.closeTimestamp);
            const isPassedCloseTime = hasCloseTime && item.closeTimestamp <= Date.now();
            const isOpenDisplay = item.value && !isPassedCloseTime;

            return (
              <div
                key={item.key}
                className={`p-3.5 rounded-md border transition-all flex flex-col justify-between shadow-2xs ${isOpenDisplay
                  ? "bg-emerald-50/70 border-emerald-300 text-emerald-900"
                  : "bg-gray-50 border-gray-200 text-gray-600"
                  }`}
              >
                {/* Header Row: Name & ON/OFF Toggle */}
                <div
                  onClick={() =>
                    !updatingConfigKey && updateGeneralConfig(item.key, !item.value)
                  }
                  className="flex items-center justify-between cursor-pointer select-none"
                  title="คลิกเพื่อเปิด/ปิดขายทันที"
                >
                  <div>
                    <p className="text-xs font-bold text-gray-500">{item.name}</p>
                    <p className="text-base font-bold mt-0.5">
                      {isOpenDisplay ? "เปิดขาย" : "ปิดขาย"}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 text-xs font-bold rounded-md text-white flex items-center gap-1.5 ${isOpenDisplay ? "bg-emerald-600" : "bg-gray-400"
                      }`}
                  >
                    {updatingConfigKey === item.key ? (
                      <>
                        <Spinner className="w-3 h-3" color="border-white" />
                        <span>กำลังบันทึก...</span>
                      </>
                    ) : isOpenDisplay ? (
                      "ON"
                    ) : (
                      "OFF"
                    )}
                  </span>
                </div>

                {/* Scheduled Close Row */}
                <div className="mt-2.5 pt-2 border-t border-gray-200/80 flex flex-col gap-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-semibold text-gray-500 flex items-center gap-1">
                      <FiCalendar className="text-[11px]" />
                      <span>เวลาปิดขาย:</span>
                    </span>
                    {hasCloseTime && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateGeneralConfig(`closeTime.${item.type}`, null);
                        }}
                        className="text-[10px] text-rose-500 hover:text-rose-700 font-semibold cursor-pointer"
                        title="ยกเลิกการตั้งเวลาปิด"
                      >
                        ล้างเวลา
                      </button>
                    )}
                  </div>

                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <DatePicker
                      showTime={{ format: "HH:mm" }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="ตั้งเวลาปิด..."
                      size="small"
                      value={item.closeTimestamp ? dayjs(item.closeTimestamp) : null}
                      onChange={(date) => {
                        updateGeneralConfig(
                          `closeTime.${item.type}`,
                          date ? date.valueOf() : null
                        );
                      }}
                      className="w-full text-xs font-medium rounded-md"
                    />
                  </div>

                  {hasCloseTime && (
                    <p className="text-[10px] text-slate-500 truncate">
                      {isPassedCloseTime ? (
                        <span className="text-rose-600 font-bold">● เลยเวลาปิดแล้ว</span>
                      ) : (
                        <span>ปิด: {dayjs(item.closeTimestamp).format("DD/MM/YY HH:mm")} น.</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Category Navigation Pills (Redesigned) ────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {CATEGORIES.map((cat) => {
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                setActiveCategory(cat.id);
                setShowCreate(false);
                setShowBulkCreate(false);
                setSelectedCoupon(false);
                setShowCreateBundle(false);
                setSelectedBundle(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-md transition-all cursor-pointer shadow-2xs ${isActive
                ? "bg-blue text-white shadow-xs"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
            >
              {cat.icon}
              <span>{cat.name}</span>
            </button>
          );
        })}
      </div>

      {/* ─── Drawers / Modals (Create & Edit) ───────────────────────────────── */}
      {showCreate && (
        <CreateCouponDrawer
          setShowCreate={setShowCreate}
          getCoupons={getCoupons}
          defaultType={activeCategory === "bundle" ? "voucher" : activeCategory}
        />
      )}

      {selectedCoupon && (
        <EditCouponDrawer
          coupon={selectedCoupon}
          setSelectedCoupon={setSelectedCoupon}
          getCoupons={getCoupons}
        />
      )}

      {showBulkCreate && (
        <BulkCreateModal
          setShowBulkCreate={setShowBulkCreate}
          getCoupons={getCoupons}
        />
      )}

      {showCreateBundle && (
        <CreateBundleDrawer
          setShowCreateBundle={setShowCreateBundle}
          getBundles={getBundles}
        />
      )}

      {selectedBundle && (
        <EditBundleDrawer
          bundle={selectedBundle}
          setSelectedBundle={setSelectedBundle}
          getBundles={getBundles}
        />
      )}

      {/* ─── Tables Content ────────────────────────────────────────────────── */}
      {activeCategory === "bundle" ? (
        /* ─── BUNDLES TABLE ─── */
        <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50/60">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">ชุดแพ็กเกจสลาก</h2>
              <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
                {bundles ? bundles.length : 0} รายการ
              </span>
            </div>
            <button
              type="button"
              className="bg-blue flex gap-1.5 items-center rounded-md py-2 px-3.5 text-white font-bold text-sm hover:bg-blue/90 duration-150 cursor-pointer shadow-xs"
              onClick={() => {
                setShowCreateBundle(true);
                setSelectedBundle(false);
              }}
            >
              <FaPlus className="text-xs" /> สร้างชุดแพ็กเกจใหม่
            </button>
          </div>

          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-100/90 border-b border-gray-200">
                <TableRow>
                  <TableHead className="py-3 font-bold text-gray-700">วันหมดอายุ</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">รูปภาพ</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">ชื่อชุดแพ็กเกจ</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">ราคา</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">รายการในชุด</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">สถานะ</TableHead>
                  <TableHead className="text-right py-3 font-bold text-gray-700">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan="7">
                      <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                        <Spinner className="w-8 h-8" />
                        <p className="text-sm font-semibold">กำลังโหลดข้อมูลชุดแพ็กเกจ...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (bundles || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="7">
                      <div className="w-full h-64 flex justify-center items-center text-gray-400 font-semibold">
                        ไม่พบข้อมูลชุดแพ็กเกจ
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  bundles.map((bundle) => (
                    <BundleRow
                      key={bundle.id}
                      bundle={bundle}
                      setSelectedBundle={setSelectedBundle}
                      getBundles={getBundles}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        /* ─── COUPONS TABLE ─── */
        <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50/60 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">
                {CATEGORIES.find((c) => c.id === activeCategory)?.name || "คูปอง"}
              </h2>
              <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
                {coupons ? coupons.length : 0} รายการ
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {activeCategory === "voucher-lock" && (
                <>
                  <button
                    type="button"
                    className="bg-amber-600 flex gap-1.5 items-center rounded-md py-2 px-3 text-white font-bold text-sm hover:opacity-85 duration-150 cursor-pointer shadow-xs"
                    onClick={() => setShowBulkCreate(true)}
                  >
                    <FaPlus className="text-xs" /> สร้างล็อคเลข 00–99
                  </button>
                  <button
                    type="button"
                    className="bg-rose-700 hover:opacity-85 duration-150 px-3 py-2 rounded-md text-white font-bold text-sm cursor-pointer shadow-xs"
                    onClick={handleDeleteVoucherLockAll}
                  >
                    ลบทั้งหมด
                  </button>
                </>
              )}

              <button
                type="button"
                className="bg-blue flex gap-1.5 items-center rounded-md py-2 px-3.5 text-white font-bold text-sm hover:bg-blue/90 duration-150 cursor-pointer shadow-xs"
                onClick={() => {
                  setShowCreate(true);
                  setSelectedCoupon(false);
                }}
              >
                <FaPlus className="text-xs" /> สร้างคูปองใหม่
              </button>

              <button
                type="button"
                disabled={!!bulkStatusLoading}
                className="bg-emerald-600 hover:bg-emerald-700 duration-150 px-3 py-2 rounded-md text-white font-bold text-sm cursor-pointer shadow-xs flex items-center gap-1.5 disabled:opacity-60"
                onClick={() => handleBulkCouponStatus(statuses.ACTIVE)}
              >
                {bulkStatusLoading === statuses.ACTIVE ? (
                  <>
                    <Spinner className="w-3.5 h-3.5" color="border-white" />
                    <span>กำลังเปิด...</span>
                  </>
                ) : (
                  "เปิดทั้งหมด"
                )}
              </button>
              <button
                type="button"
                disabled={!!bulkStatusLoading}
                className="bg-rose-600 hover:bg-rose-700 duration-150 px-3 py-2 rounded-md text-white font-bold text-sm cursor-pointer shadow-xs flex items-center gap-1.5 disabled:opacity-60"
                onClick={() => handleBulkCouponStatus(statuses.INACTIVE)}
              >
                {bulkStatusLoading === statuses.INACTIVE ? (
                  <>
                    <Spinner className="w-3.5 h-3.5" color="border-white" />
                    <span>กำลังปิด...</span>
                  </>
                ) : (
                  "ปิดทั้งหมด"
                )}
              </button>
            </div>
          </div>

          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-100/90 border-b border-gray-200">
                <TableRow>
                  <TableHead className="py-3 font-bold text-gray-700">วันหมดอายุ</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">รูปภาพ</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">ชื่อคูปอง</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">ราคา</TableHead>
                  <TableHead className="text-center py-3 font-bold text-gray-700">สถานะ</TableHead>
                  <TableHead className="text-right py-3 font-bold text-gray-700">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan="6">
                      <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                        <Spinner className="w-8 h-8" />
                        <p className="text-sm font-semibold">กำลังโหลดข้อมูลคูปอง...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (coupons || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="6">
                      <div className="w-full h-64 flex justify-center items-center text-gray-400 font-semibold">
                        ไม่พบข้อมูลคูปองในหมวดนี้
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  (coupons || [])
                    .sort(
                      (a, b) =>
                        (a.lockedDigits || 0) - (b.lockedDigits || 0) ||
                        (b.expireAt || 0) - (a.expireAt || 0),
                    )
                    .map((coupon) => (
                      <CouponRow
                        key={coupon.id}
                        coupon={coupon}
                        setSelectedCoupon={setSelectedCoupon}
                        getCoupons={getCoupons}
                      />
                    ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Coupon Row ───────────────────────────────────────────────────────────────

const CouponRow = ({ coupon, setSelectedCoupon, getCoupons }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const updateChange = async (key, value) => {
    if (!window.confirm("ท่านต้องการแก้ไขใช่หรือไม่")) return;
    setLoading(true);
    const { status } = await callApi(
      "coupons-editCoupon",
      { id: coupon.id, key, value },
      { notShowLoading: true },
    );
    status
      ? toast({ className: "bg-[rgb(59,176,101)]", title: "อัพเดตข้อมูลสำเร็จ" })
      : toast({ className: "bg-[#c15b78]", title: "อัพเดตข้อมูลไม่สำเร็จ" });
    setLoading(false);
    getCoupons();
  };

  return (
    <TableRow className="hover:bg-slate-50/70 transition-colors">
      <TableCell className="w-[180px] font-semibold text-gray-600 text-sm">
        {coupon?.expireAt
          ? dayjs(coupon.expireAt).format("YYYY-MM-DD HH:mm")
          : "-"}
      </TableCell>
      <TableCell className="text-center">
        {coupon.image ? (
          <img
            src={coupon.image}
            alt={coupon.name}
            className="w-12 h-12 object-cover rounded-md mx-auto shadow-2xs border border-gray-200"
          />
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        )}
      </TableCell>
      <TableCell className="text-center font-bold text-gray-800 text-sm">
        {coupon.name}
      </TableCell>
      <TableCell className="text-center font-semibold text-gray-700 text-sm">
        {coupon.isPointRedeem ? `${coupon.point} คะแนน` : `${coupon.price} บาท`}
      </TableCell>
      <TableCell className="text-center">
        <div className="relative inline-flex items-center justify-center min-w-[100px]">
          {loading ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 text-slate-800 text-xs font-bold shadow-2xs border border-slate-200">
              <Spinner className="w-3.5 h-3.5" />
              <span>กำลังเปลี่ยน...</span>
            </div>
          ) : (
            <select
              disabled={loading}
              className={`${coupon.status === constant.statuses.ACTIVE
                ? "bg-emerald-600"
                : coupon.status === constant.statuses.OFFLINE
                  ? "bg-amber-500"
                  : "bg-rose-600"
                } text-white font-bold text-xs py-1.5 px-3 rounded-md cursor-pointer border-0 shadow-2xs focus:outline-none transition-all`}
              value={coupon.status}
              onChange={(e) => updateChange("status", e.target.value)}
            >
              <option value={constant.statuses.ACTIVE}>
                {constant.statusesTranslate.active}
              </option>
              <option value={constant.statuses.OFFLINE}>
                {constant.statusesTranslate.offline}
              </option>
              <option value={constant.statuses.INACTIVE}>
                {constant.statusesTranslate.inactive}
              </option>
            </select>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <button
          type="button"
          className="bg-blue text-white text-xs px-3 py-1.5 rounded-md font-bold hover:bg-blue/90 shadow-2xs cursor-pointer transition-all"
          onClick={() => setSelectedCoupon(coupon)}
        >
          แก้ไข
        </button>
      </TableCell>
    </TableRow>
  );
};

// ─── Bundle Row ───────────────────────────────────────────────────────────────

const BundleRow = ({ bundle, setSelectedBundle, getBundles }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const updateChange = async (key, value) => {
    if (!window.confirm("ท่านต้องการแก้ไขใช่หรือไม่")) return;
    setLoading(true);
    const { status } = await callApi(
      "coupons-editBundle",
      { id: bundle.id, key, value },
      { notShowLoading: true },
    );
    status
      ? toast({ className: "bg-[rgb(59,176,101)]", title: "อัพเดตข้อมูลสำเร็จ" })
      : toast({ className: "bg-[#c15b78]", title: "อัพเดตข้อมูลไม่สำเร็จ" });
    setLoading(false);
    getBundles();
  };

  return (
    <TableRow className="hover:bg-slate-50/70 transition-colors">
      <TableCell className="w-[180px] font-semibold text-gray-600 text-sm">
        {bundle?.expireAt ? dayjs(bundle.expireAt).format("YYYY-MM-DD HH:mm") : "-"}
      </TableCell>
      <TableCell className="text-center">
        {bundle.image ? (
          <img
            src={bundle.image}
            alt={bundle.name}
            className="w-12 h-12 object-cover rounded-md mx-auto shadow-2xs border border-gray-200"
          />
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        )}
      </TableCell>
      <TableCell className="text-center font-bold text-gray-800 text-sm">
        {bundle.name}
      </TableCell>
      <TableCell className="text-center font-semibold text-gray-700 text-sm">
        {bundle.price} {bundle.isPointRedeem ? "คะแนน" : "บาท"}
      </TableCell>
      <TableCell className="text-center">
        <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200">
          {(bundle.items || []).length} รายการ
        </span>
      </TableCell>
      <TableCell className="text-center">
        <div className="relative inline-flex items-center justify-center min-w-[100px]">
          {loading ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 text-slate-800 text-xs font-bold shadow-2xs border border-slate-200">
              <Spinner className="w-3.5 h-3.5" />
              <span>กำลังเปลี่ยน...</span>
            </div>
          ) : (
            <select
              disabled={loading}
              className={`${bundle.status === constant.statuses.ACTIVE
                ? "bg-emerald-600"
                : bundle.status === constant.statuses.OFFLINE
                  ? "bg-amber-500"
                  : "bg-rose-600"
                } text-white font-bold text-xs py-1.5 px-3 rounded-md cursor-pointer border-0 shadow-2xs focus:outline-none transition-all`}
              value={bundle.status}
              onChange={(e) => updateChange("status", e.target.value)}
            >
              <option value={constant.statuses.ACTIVE}>
                {constant.statusesTranslate.active}
              </option>
              <option value={constant.statuses.OFFLINE}>
                {constant.statusesTranslate.offline}
              </option>
              <option value={constant.statuses.INACTIVE}>
                {constant.statusesTranslate.inactive}
              </option>
            </select>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <button
          type="button"
          className="bg-blue text-white text-xs px-3 py-1.5 rounded-md font-bold hover:bg-blue/90 shadow-2xs cursor-pointer transition-all"
          onClick={() => setSelectedBundle(bundle)}
        >
          แก้ไข
        </button>
      </TableCell>
    </TableRow>
  );
};

// ─── Create Coupon Drawer ─────────────────────────────────────────────────────

const CreateCouponDrawer = ({ setShowCreate, getCoupons, defaultType }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [coupon, setCoupon] = useState({
    status: statuses.ACTIVE,
    type: defaultType === "vip" ? "voucher" : defaultType || "voucher",
    vip: defaultType === "vip",
    name: "",
    description: "",
    price: "",
    isPointRedeem: false,
    point: "",
    discount: "",
    limitPerUsed: "",
    limitPerRounddate: "",
    limitPerDay: "",
    expireAt: null,
    conditions: [],
    reserveType: "lastTwo",
  });
  const [previewImage, setPreviewImage] = useState(false);
  const [coverImage, setCoverImage] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key, value) => setCoupon((prev) => ({ ...prev, [key]: value }));

  const handleImageUpload = async (e, field, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    if (base64) {
      setPreview(URL.createObjectURL(file));
      set(field, base64);
    }
  };

  const handleCreate = async () => {
    if (!coupon.name || !coupon.expireAt) {
      alert("กรุณากรอกชื่อและวันหมดอายุ");
      return;
    }
    setLoading(true);
    const result = await callApi("coupons-createCoupon", coupon, {
      notShowLoading: true,
    });
    if (result.status) {
      getCoupons();
      toast({ className: "bg-[rgb(59,176,101)]", title: "สร้างคูปองสำเร็จ" });
      setShowCreate(false);
    } else {
      toast({ className: "bg-[#c15b78]", title: result.message || "สร้างไม่สำเร็จ" });
    }
    setLoading(false);
  };

  return (
    <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
      {loading && (
        <div className="z-50 bg-[rgba(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-8 h-8" />
        </div>
      )}
      <div className="flex items-center p-4 border-b border-gray-200 justify-between bg-gray-50/60">
        <h3 className="font-bold text-lg text-gray-800">สร้างคูปองใหม่</h3>
        <button
          type="button"
          onClick={() => setShowCreate(false)}
          className="text-gray-400 hover:text-red-600 p-1 cursor-pointer transition-colors"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Images */}
        <div className="space-y-3 border p-3 rounded-md border-gray-200 bg-gray-50/50">
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปคูปอง</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {previewImage ? (
                <img src={previewImage} alt="preview" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">
                  <TbPhotoPlus className="text-2xl mx-auto mb-1" />
                  <span>คลิกเพื่ออัพโหลดรูปคูปอง</span>
                </div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "image", setPreviewImage)}
              />
            </label>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปปก (Preview)</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {coverImage ? (
                <img src={coverImage} alt="cover" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">
                  <TbPhotoPlus className="text-2xl mx-auto mb-1" />
                  <span>คลิกเพื่ออัพโหลดรูปปก</span>
                </div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "previewImage", setCoverImage)}
              />
            </label>
          </div>
        </div>

        {/* Form Fields */}
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="ประเภทคูปอง">
            <select
              className={selectCls}
              value={coupon.type}
              onChange={(e) => set("type", e.target.value)}
            >
              <option value="voucher">คูปองสลาก (Voucher)</option>
              <option value="discount">คูปองส่วนลด (Discount)</option>
              <option value="reserve">คูปองจองสิทธิ์ (Reserve)</option>
              <option value="voucher-lock">คูปองสลากล็อคเลข</option>
            </select>
          </Field>

          <Field label="ชื่อคูปอง *">
            <input
              className={inputCls}
              type="text"
              value={coupon.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ระบุชื่อคูปอง"
            />
          </Field>

          <Field label="คำอธิบาย">
            <input
              className={inputCls}
              type="text"
              value={coupon.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="คำอธิบายคูปอง"
            />
          </Field>

          <Field label="ชำระโดย">
            <select
              className={selectCls}
              value={coupon.isPointRedeem ? "T" : "F"}
              onChange={(e) => set("isPointRedeem", e.target.value === "T")}
            >
              <option value="F">โอนเงิน (บาท)</option>
              <option value="T">แลกคะแนน (คะแนน)</option>
            </select>
          </Field>

          {coupon.isPointRedeem ? (
            <Field label="คะแนนที่ใช้แลก">
              <input
                className={inputCls}
                type="number"
                value={coupon.point}
                onChange={(e) => set("point", Number(e.target.value))}
                placeholder="ระบุคะแนน"
              />
            </Field>
          ) : (
            <Field label="ราคา (บาท)">
              <input
                className={inputCls}
                type="number"
                value={coupon.price}
                onChange={(e) => set("price", Number(e.target.value))}
                placeholder="ระบุราคา"
              />
            </Field>
          )}

          {coupon.type === "discount" && (
            <>
              <Field label="ส่วนลด (บาท)">
                <input
                  className={inputCls}
                  type="number"
                  value={coupon.discount}
                  onChange={(e) => set("discount", Number(e.target.value))}
                  placeholder="จำนวนเงินส่วนลด"
                />
              </Field>
              <Field label="ยอดสั่งซื้อขั้นต่ำ (บาท)">
                <input
                  className={inputCls}
                  type="number"
                  value={coupon.minimum || ""}
                  onChange={(e) => set("minimum", Number(e.target.value))}
                  placeholder="ยอดสั่งซื้อขั้นต่ำ (0 = ไม่จำกัด)"
                />
              </Field>
            </>
          )}

          {coupon.type === "reserve" && (
            <>
              <Field label="ประเภทเลขจอง">
                <select
                  className={selectCls}
                  value={coupon.reserveType}
                  onChange={(e) => set("reserveType", e.target.value)}
                >
                  <option value="lastTwo">เลขท้าย 2 ตัว</option>
                  <option value="lastThree">เลขท้าย 3 ตัว</option>
                </select>
              </Field>
              <Field label="จำนวนสิทธิ์">
                <input
                  className={inputCls}
                  type="number"
                  value={coupon.quantity || ""}
                  onChange={(e) => set("quantity", Number(e.target.value))}
                  placeholder="จำนวนสิทธิ์"
                />
              </Field>
              <Field label="จำกัดจำนวนต่อครั้ง">
                <input
                  className={inputCls}
                  type="number"
                  value={coupon.limitPerUsed || ""}
                  onChange={(e) => set("limitPerUsed", Number(e.target.value))}
                  placeholder="จำกัดต่อครั้ง"
                />
              </Field>
            </>
          )}

          {coupon.type === "voucher-lock" && (
            <>
              <Field label="ประเภทล็อคเลข">
                <select
                  className={selectCls}
                  value={coupon.lockType || "lastTwo"}
                  onChange={(e) => set("lockType", e.target.value)}
                >
                  <option value="lastTwo">เลขท้าย 2 ตัว</option>
                  <option value="lastThree">เลขท้าย 3 ตัว</option>
                </select>
              </Field>
              <Field label="เลขที่ล็อค">
                <input
                  className={inputCls}
                  type="text"
                  maxLength={coupon.lockType === "lastThree" ? 3 : 2}
                  value={coupon.lockedDigits || ""}
                  onChange={(e) => set("lockedDigits", e.target.value)}
                  placeholder={coupon.lockType === "lastThree" ? "เช่น 123" : "เช่น 00"}
                />
              </Field>
            </>
          )}

          <Field label="จำกัดสิทธิ์ต่องวด (ครั้ง)">
            <input
              className={inputCls}
              type="number"
              value={coupon.limitPerRounddate || ""}
              onChange={(e) => set("limitPerRounddate", Number(e.target.value))}
              placeholder="0 = ไม่จำกัด"
            />
          </Field>

          <Field label="จำกัดสิทธิ์ต่อวัน (ครั้ง)">
            <input
              className={inputCls}
              type="number"
              value={coupon.limitPerDay || ""}
              onChange={(e) => set("limitPerDay", Number(e.target.value))}
              placeholder="0 = ไม่จำกัด"
            />
          </Field>

          <Field label="วันหมดอายุ *">
            <DatePicker
              className="border-1 w-full h-[40px] border-gray-300 font-semibold rounded-md text-sm"
              showTime
              placeholder="เลือกวันหมดอายุ"
              value={coupon.expireAt ? dayjs(coupon.expireAt) : null}
              onChange={(date) => set("expireAt", date ? date.valueOf() : null)}
            />
          </Field>

          <div className="flex items-center gap-2 pt-6">
            <input
              id="new-coupon-vip"
              type="checkbox"
              className="cursor-pointer w-4 h-4 rounded"
              checked={coupon.vip}
              onChange={() => set("vip", !coupon.vip)}
            />
            <label htmlFor="new-coupon-vip" className="font-bold text-sm text-gray-700 cursor-pointer">
              VIP เท่านั้น
            </label>
          </div>

          <ConditionsEditor
            conditions={coupon.conditions || []}
            onChange={(conds) => set("conditions", conds)}
          />
        </div>
      </div>

      <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50/50 justify-end">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md font-bold text-sm text-gray-700 cursor-pointer transition-all"
          onClick={() => setShowCreate(false)}
        >
          ยกเลิก
        </button>
        <button
          type="button"
          className="px-5 py-2 bg-blue hover:bg-blue/90 rounded-md font-bold text-sm text-white cursor-pointer transition-all shadow-xs"
          onClick={handleCreate}
        >
          สร้างคูปอง
        </button>
      </div>
    </div>
  );
};

// ─── Edit Coupon Drawer ───────────────────────────────────────────────────────

const EditCouponDrawer = ({ coupon, setSelectedCoupon, getCoupons }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [form, setForm] = useState({
    ...coupon,
    type: coupon.type || "voucher",
    status: coupon.status || statuses.ACTIVE,
    name: coupon.name || "",
    description: coupon.description || "",
    isPointRedeem: coupon.isPointRedeem === true,
    price: coupon.price ?? "",
    point: coupon.point ?? "",
    discount: coupon.discount ?? "",
    minimum: coupon.minimum ?? "",
    quantity: coupon.quantity ?? "",
    limitPerUsed: coupon.limitPerUsed ?? "",
    limitPerRounddate: coupon.limitPerRounddate ?? "",
    limitPerDay: coupon.limitPerDay ?? "",
    reserveType: coupon.reserveType || "lastTwo",
    lockType: coupon.lockType || "lastTwo",
    lockedDigits: coupon.lockedDigits || "",
    vip: coupon.vip === true,
    conditions: Array.isArray(coupon.conditions) ? coupon.conditions : [],
  });
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(coupon.image);
  const [coverImage, setCoverImage] = useState(coupon.previewImage);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleImageUpload = async (e, field, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    if (base64) {
      setPreview(URL.createObjectURL(file));
      set(field, base64);
    }
  };

  const handleEdit = async () => {
    if (!form.name || !form.expireAt) {
      alert("กรุณากรอกชื่อและวันหมดอายุ");
      return;
    }
    setLoading(true);
    const result = await callApi("coupons-editCoupon", form, {
      notShowLoading: true,
    });
    if (result.status) {
      getCoupons();
      toast({ className: "bg-[rgb(59,176,101)]", title: "บันทึกข้อมูลสำเร็จ" });
      setSelectedCoupon(false);
    } else {
      toast({ className: "bg-[#c15b78]", title: result.message || "บันทึกไม่สำเร็จ" });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("ต้องการลบคูปองนี้ใช่หรือไม่?")) return;
    setLoading(true);
    const result = await callApi(
      "coupons-deleteCoupon",
      { id: form.id },
      { notShowLoading: true },
    );
    if (result.status) {
      getCoupons();
      toast({ className: "bg-[rgb(59,176,101)]", title: "ลบคูปองสำเร็จ" });
      setSelectedCoupon(false);
    }
    setLoading(false);
  };

  return (
    <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
      {loading && (
        <div className="z-50 bg-[rgba(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-8 h-8" />
        </div>
      )}
      <div className="flex items-center p-4 border-b border-gray-200 justify-between bg-gray-50/60">
        <h3 className="font-bold text-lg text-gray-800">แก้ไขคูปอง: {form.name}</h3>
        <button
          type="button"
          onClick={() => setSelectedCoupon(false)}
          className="text-gray-400 hover:text-red-600 p-1 cursor-pointer transition-colors"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Images */}
        <div className="space-y-3 border p-3 rounded-md border-gray-200 bg-gray-50/50">
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปคูปอง</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {previewImage ? (
                <img src={previewImage} alt="preview" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูป</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "image", setPreviewImage)}
              />
            </label>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปปก (Preview)</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {coverImage ? (
                <img src={coverImage} alt="cover" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูป</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "previewImage", setCoverImage)}
              />
            </label>
          </div>
        </div>

        {/* Fields */}
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="ประเภทคูปอง">
            <select
              className={selectCls}
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
            >
              <option value="voucher">คูปองสลาก (Voucher)</option>
              <option value="discount">คูปองส่วนลด (Discount)</option>
              <option value="reserve">คูปองจองสิทธิ์ (Reserve)</option>
              <option value="voucher-lock">คูปองสลากล็อคเลข</option>
            </select>
          </Field>

          <Field label="สถานะคูปอง">
            <select
              className={selectCls}
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value={statuses.ACTIVE}>{constant.statusesTranslate.active}</option>
              <option value={statuses.OFFLINE}>{constant.statusesTranslate.offline}</option>
              <option value={statuses.INACTIVE}>{constant.statusesTranslate.inactive}</option>
            </select>
          </Field>

          <Field label="ชื่อคูปอง *">
            <input
              className={inputCls}
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ระบุชื่อคูปอง"
            />
          </Field>

          <Field label="คำอธิบาย">
            <input
              className={inputCls}
              type="text"
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="คำอธิบายคูปอง"
            />
          </Field>

          <Field label="ชำระโดย">
            <select
              className={selectCls}
              value={form.isPointRedeem ? "T" : "F"}
              onChange={(e) => set("isPointRedeem", e.target.value === "T")}
            >
              <option value="F">โอนเงิน (บาท)</option>
              <option value="T">แลกคะแนน (คะแนน)</option>
            </select>
          </Field>

          {form.isPointRedeem ? (
            <Field label="คะแนนที่ใช้แลก">
              <input
                className={inputCls}
                type="number"
                value={form.point}
                onChange={(e) => set("point", Number(e.target.value))}
                placeholder="ระบุคะแนน"
              />
            </Field>
          ) : (
            <Field label="ราคา (บาท)">
              <input
                className={inputCls}
                type="number"
                value={form.price}
                onChange={(e) => set("price", Number(e.target.value))}
                placeholder="ระบุราคา"
              />
            </Field>
          )}

          {form.type === "discount" && (
            <>
              <Field label="ส่วนลด (บาท)">
                <input
                  className={inputCls}
                  type="number"
                  value={form.discount}
                  onChange={(e) => set("discount", Number(e.target.value))}
                  placeholder="จำนวนเงินส่วนลด"
                />
              </Field>
              <Field label="ยอดสั่งซื้อขั้นต่ำ (บาท)">
                <input
                  className={inputCls}
                  type="number"
                  value={form.minimum || ""}
                  onChange={(e) => set("minimum", Number(e.target.value))}
                  placeholder="ยอดสั่งซื้อขั้นต่ำ (0 = ไม่จำกัด)"
                />
              </Field>
            </>
          )}

          {form.type === "reserve" && (
            <>
              <Field label="ประเภทเลขจอง">
                <select
                  className={selectCls}
                  value={form.reserveType}
                  onChange={(e) => set("reserveType", e.target.value)}
                >
                  <option value="lastTwo">เลขท้าย 2 ตัว</option>
                  <option value="lastThree">เลขท้าย 3 ตัว</option>
                </select>
              </Field>
              <Field label="จำนวนสิทธิ์">
                <input
                  className={inputCls}
                  type="number"
                  value={form.quantity || ""}
                  onChange={(e) => set("quantity", Number(e.target.value))}
                  placeholder="จำนวนสิทธิ์"
                />
              </Field>
              <Field label="จำกัดจำนวนต่อครั้ง">
                <input
                  className={inputCls}
                  type="number"
                  value={form.limitPerUsed || ""}
                  onChange={(e) => set("limitPerUsed", Number(e.target.value))}
                  placeholder="จำกัดต่อครั้ง"
                />
              </Field>
            </>
          )}

          {form.type === "voucher-lock" && (
            <>
              <Field label="ประเภทล็อคเลข">
                <select
                  className={selectCls}
                  value={form.lockType || "lastTwo"}
                  onChange={(e) => set("lockType", e.target.value)}
                >
                  <option value="lastTwo">เลขท้าย 2 ตัว</option>
                  <option value="lastThree">เลขท้าย 3 ตัว</option>
                </select>
              </Field>
              <Field label="เลขที่ล็อค">
                <input
                  className={inputCls}
                  type="text"
                  maxLength={form.lockType === "lastThree" ? 3 : 2}
                  value={form.lockedDigits || ""}
                  onChange={(e) => set("lockedDigits", e.target.value)}
                  placeholder={form.lockType === "lastThree" ? "เช่น 123" : "เช่น 00"}
                />
              </Field>
            </>
          )}

          <Field label="จำกัดสิทธิ์ต่องวด (ครั้ง)">
            <input
              className={inputCls}
              type="number"
              value={form.limitPerRounddate || ""}
              onChange={(e) => set("limitPerRounddate", Number(e.target.value))}
              placeholder="0 = ไม่จำกัด"
            />
          </Field>

          <Field label="จำกัดสิทธิ์ต่อวัน (ครั้ง)">
            <input
              className={inputCls}
              type="number"
              value={form.limitPerDay || ""}
              onChange={(e) => set("limitPerDay", Number(e.target.value))}
              placeholder="0 = ไม่จำกัด"
            />
          </Field>

          <Field label="วันหมดอายุ *">
            <DatePicker
              className="border-1 w-full h-[40px] border-gray-300 font-semibold rounded-md text-sm"
              showTime
              placeholder="วันหมดอายุ"
              value={form.expireAt ? dayjs(form.expireAt) : null}
              onChange={(date) => set("expireAt", date ? date.valueOf() : null)}
            />
          </Field>

          <div className="flex items-center gap-2 pt-6">
            <input
              id="edit-coupon-vip"
              type="checkbox"
              className="cursor-pointer w-4 h-4 rounded"
              checked={form.vip || false}
              onChange={() => set("vip", !form.vip)}
            />
            <label htmlFor="edit-coupon-vip" className="font-bold text-sm text-gray-700 cursor-pointer">
              VIP เท่านั้น
            </label>
          </div>

          <ConditionsEditor
            conditions={form.conditions || []}
            onChange={(conds) => set("conditions", conds)}
          />
        </div>
      </div>

      <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50/50 justify-between items-center">
        <button
          type="button"
          className="p-2.5 text-red-600 hover:bg-red-50 rounded-md font-bold text-sm flex items-center gap-1.5 cursor-pointer transition-colors"
          onClick={handleDelete}
        >
          <MdOutlineDeleteOutline className="text-lg" /> ลบคูปองนี้
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md font-bold text-sm text-gray-700 cursor-pointer transition-all"
            onClick={() => setSelectedCoupon(false)}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            className="px-5 py-2 bg-blue hover:bg-blue/90 rounded-md font-bold text-sm text-white cursor-pointer transition-all shadow-xs"
            onClick={handleEdit}
          >
            บันทึกการแก้ไข
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Bulk Create Modal ────────────────────────────────────────────────────────

const BulkCreateModal = ({ setShowBulkCreate, getCoupons }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "คูปองสลากล็อคเลข",
    description: "",
    price: 80,
    expireAt: null,
    conditions: [],
  });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleBulkCreate = async () => {
    if (!form.expireAt) {
      alert("กรุณาเลือกวันหมดอายุ");
      return;
    }
    setLoading(true);
    const promises = [];
    for (let i = 0; i <= 99; i++) {
      const numStr = String(i).padStart(2, "0");
      promises.push(
        callApi(
          "coupons-createCoupon",
          {
            ...form,
            name: `${form.name} ${numStr}`,
            type: "voucher-lock",
            lockedDigits: numStr,
            status: statuses.ACTIVE,
          },
          { notShowLoading: true },
        ),
      );
    }

    const results = await Promise.allSettled(promises);
    const ok = results.filter((r) => r.value?.status).length;
    setLoading(false);
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: `สร้างคูปองล็อคเลข 00–99 สำเร็จ ${ok} ใบ`,
    });
    getCoupons();
    setShowBulkCreate(false);
  };

  return (
    <div className="bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
        <h3 className="font-bold text-lg text-gray-800">
          สร้างคูปองล็อคเลข 00–99 (100 ใบ อัตโนมัติ)
        </h3>
        <button
          type="button"
          onClick={() => setShowBulkCreate(false)}
          className="text-gray-400 hover:text-red-600 cursor-pointer"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="ชื่อตั้งต้น">
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="ราคา (บาท)">
          <input
            className={inputCls}
            type="number"
            value={form.price}
            onChange={(e) => set("price", Number(e.target.value))}
          />
        </Field>
        <Field label="วันหมดอายุ *">
          <DatePicker
            className="border-1 w-full h-[40px] border-gray-300 font-semibold rounded-md text-sm"
            showTime
            placeholder="เลือกวันหมดอายุ"
            value={form.expireAt ? dayjs(form.expireAt) : null}
            onChange={(date) => set("expireAt", date ? date.valueOf() : null)}
          />
        </Field>
        <ConditionsEditor
          conditions={form.conditions}
          onChange={(conds) => set("conditions", conds)}
        />
      </div>

      <div className="flex gap-2 mt-4 pt-3 border-t border-gray-200 justify-end">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md font-bold text-sm text-gray-700 cursor-pointer"
          onClick={() => setShowBulkCreate(false)}
        >
          ยกเลิก
        </button>
        <button
          type="button"
          disabled={loading}
          className="px-5 py-2 bg-blue hover:bg-blue/90 text-white rounded-md font-bold text-sm disabled:opacity-50 cursor-pointer shadow-xs"
          onClick={handleBulkCreate}
        >
          {loading ? "กำลังสร้าง 100 ใบ..." : "สร้างคูปอง 100 ใบ"}
        </button>
      </div>
    </div>
  );
};

// ─── Create Bundle Drawer ─────────────────────────────────────────────────────

const CreateBundleDrawer = ({ setShowCreateBundle, getBundles }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [bundle, setBundle] = useState({
    status: statuses.ACTIVE,
    name: "",
    price: "",
    isPointRedeem: false,
    quantity: "",
    limitPerRounddate: "",
    limitPerDay: "",
    expireAt: null,
    vip: false,
    items: [],
  });
  const [allCoupons, setAllCoupons] = useState([]);
  const [previewImage, setPreviewImage] = useState(false);
  const [coverImage, setCoverImage] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    callApi("coupons-getAllCoupon", { limit: 500 }, { notShowLoading: true }).then(
      (res) => setAllCoupons(res || []),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setBundle((prev) => ({ ...prev, [key]: value }));

  const handleImageUpload = async (e, field, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    if (base64) {
      setPreview(URL.createObjectURL(file));
      set(field, base64);
    }
  };

  const addItem = () =>
    set("items", [
      ...(bundle.items || []),
      { couponId: "", couponName: "", type: "", quantity: 1 },
    ]);
  const updateItem = (index, updated) =>
    set("items", bundle.items.map((it, i) => (i === index ? updated : it)));
  const removeItem = (index) =>
    set("items", bundle.items.filter((_, i) => i !== index));

  const handleCreate = async () => {
    if (!bundle.name || !bundle.expireAt || bundle.items.length === 0) {
      alert("กรุณากรอกชื่อ วันหมดอายุ และเลือกคูปองในชุดอย่างน้อย 1 รายการ");
      return;
    }
    setLoading(true);
    const result = await callApi("coupons-createBundle", bundle, {
      notShowLoading: true,
    });
    if (result.status) {
      getBundles();
      toast({ className: "bg-[rgb(59,176,101)]", title: "สร้างชุดแพ็กเกจสำเร็จ" });
      setShowCreateBundle(false);
    } else {
      toast({ className: "bg-[#c15b78]", title: result.message || "สร้างไม่สำเร็จ" });
    }
    setLoading(false);
  };

  return (
    <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
      {loading && (
        <div className="z-50 bg-[rgba(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-8 h-8" />
        </div>
      )}
      <div className="flex items-center p-4 border-b border-gray-200 justify-between bg-gray-50/60">
        <h3 className="font-bold text-lg text-gray-800">สร้างชุดแพ็กเกจใหม่</h3>
        <button
          type="button"
          onClick={() => setShowCreateBundle(false)}
          className="text-gray-400 hover:text-red-600 p-1 cursor-pointer transition-colors"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Images */}
        <div className="space-y-3 border p-3 rounded-md border-gray-200 bg-gray-50/50">
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปชุดแพ็กเกจ</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {previewImage ? (
                <img src={previewImage} alt="preview" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูป</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "image", setPreviewImage)}
              />
            </label>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปปก (Cover)</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {coverImage ? (
                <img src={coverImage} alt="cover" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูปปก</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "previewImage", setCoverImage)}
              />
            </label>
          </div>
        </div>

        {/* Fields */}
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="ชื่อชุดแพ็กเกจ *">
            <input
              className={inputCls}
              type="text"
              value={bundle.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ระบุชื่อแพ็กเกจ"
            />
          </Field>
          <Field label="ราคา (บาท)">
            <input
              className={inputCls}
              type="number"
              value={bundle.price}
              onChange={(e) => set("price", Number(e.target.value))}
              placeholder="ระบุราคา"
            />
          </Field>
          <Field label="วันหมดอายุ *">
            <DatePicker
              className="border-1 w-full h-[40px] border-gray-300 font-semibold rounded-md text-sm"
              showTime
              placeholder="เลือกวันหมดอายุ"
              value={bundle.expireAt ? dayjs(bundle.expireAt) : null}
              onChange={(date) => set("expireAt", date ? date.valueOf() : null)}
            />
          </Field>

          {/* Bundle Items Selector */}
          <div className="col-span-1 sm:col-span-2 border border-gray-200 rounded-md p-3 bg-gray-50/50 mt-1">
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-bold text-gray-700">คูปองในชุดแพ็กเกจ</p>
              <button
                type="button"
                className="text-xs font-bold bg-blue text-white px-2.5 py-1 rounded-md hover:bg-blue/90 cursor-pointer shadow-2xs"
                onClick={addItem}
              >
                + เพิ่มคูปอง
              </button>
            </div>
            <div className="space-y-2">
              {bundle.items.map((item, index) => (
                <BundleItemRow
                  key={index}
                  index={index}
                  item={item}
                  allCoupons={allCoupons}
                  onChange={updateItem}
                  onRemove={removeItem}
                />
              ))}
              {bundle.items.length === 0 && (
                <p className="text-xs text-gray-400 py-3 text-center">
                  ยังไม่ได้เลือกคูปองในชุด กรุณากดปุ่ม + เพิ่มคูปอง
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50/50 justify-end">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md font-bold text-sm text-gray-700 cursor-pointer"
          onClick={() => setShowCreateBundle(false)}
        >
          ยกเลิก
        </button>
        <button
          type="button"
          className="px-5 py-2 bg-blue hover:bg-blue/90 text-white rounded-md font-bold text-sm cursor-pointer shadow-xs"
          onClick={handleCreate}
        >
          สร้างชุดแพ็กเกจ
        </button>
      </div>
    </div>
  );
};

// ─── Edit Bundle Drawer ───────────────────────────────────────────────────────

const EditBundleDrawer = ({ bundle, setSelectedBundle, getBundles }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [form, setForm] = useState({ ...bundle });
  const [allCoupons, setAllCoupons] = useState([]);
  const [previewImage, setPreviewImage] = useState(bundle.image);
  const [coverImage, setCoverImage] = useState(bundle.previewImage);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    callApi("coupons-getAllCoupon", { limit: 500 }, { notShowLoading: true }).then(
      (res) => setAllCoupons(res || []),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleImageUpload = async (e, field, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    if (base64) {
      setPreview(URL.createObjectURL(file));
      set(field, base64);
    }
  };

  const addItem = () =>
    set("items", [
      ...(form.items || []),
      { couponId: "", couponName: "", type: "", quantity: 1 },
    ]);
  const updateItem = (index, updated) =>
    set("items", form.items.map((it, i) => (i === index ? updated : it)));
  const removeItem = (index) =>
    set("items", form.items.filter((_, i) => i !== index));

  const handleEdit = async () => {
    setLoading(true);
    const result = await callApi("coupons-editBundle", form, {
      notShowLoading: true,
    });
    if (result.status) {
      getBundles();
      toast({ className: "bg-[rgb(59,176,101)]", title: "บันทึกข้อมูลสำเร็จ" });
      setSelectedBundle(false);
    } else {
      toast({ className: "bg-[#c15b78]", title: result.message || "บันทึกไม่สำเร็จ" });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("ต้องการลบชุดแพ็กเกจนี้ใช่หรือไม่?")) return;
    setLoading(true);
    const result = await callApi(
      "coupons-deleteBundle",
      { id: form.id },
      { notShowLoading: true },
    );
    if (result.status) {
      getBundles();
      toast({ className: "bg-[rgb(59,176,101)]", title: "ลบแพ็กเกจสำเร็จ" });
      setSelectedBundle(false);
    }
    setLoading(false);
  };

  return (
    <div className="relative bg-white w-full rounded-md shadow-md mt-4 border border-gray-200 overflow-hidden">
      {loading && (
        <div className="z-50 bg-[rgba(255,255,255,0.7)] absolute h-full w-full flex justify-center items-center">
          <Spinner className="w-8 h-8" />
        </div>
      )}
      <div className="flex items-center p-4 border-b border-gray-200 justify-between bg-gray-50/60">
        <h3 className="font-bold text-lg text-gray-800">แก้ไขชุดแพ็กเกจ: {form.name}</h3>
        <button
          type="button"
          onClick={() => setSelectedBundle(false)}
          className="text-gray-400 hover:text-red-600 p-1 cursor-pointer transition-colors"
        >
          <FaXmark className="text-lg" />
        </button>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Images */}
        <div className="space-y-3 border p-3 rounded-md border-gray-200 bg-gray-50/50">
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปชุดแพ็กเกจ</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {previewImage ? (
                <img src={previewImage} alt="preview" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูป</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "image", setPreviewImage)}
              />
            </label>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-700 mb-1">รูปปก</p>
            <label className="border-2 border-dashed border-gray-300 rounded-md p-3 flex flex-col items-center justify-center cursor-pointer hover:border-blue bg-white transition-colors">
              {coverImage ? (
                <img src={coverImage} alt="cover" className="h-28 object-contain rounded-md" />
              ) : (
                <div className="text-center text-gray-400 text-xs py-4">คลิกเลือกรูปปก</div>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleImageUpload(e, "previewImage", setCoverImage)}
              />
            </label>
          </div>
        </div>

        {/* Fields */}
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="ชื่อชุดแพ็กเกจ">
            <input
              className={inputCls}
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>
          <Field label="ราคา (บาท)">
            <input
              className={inputCls}
              type="number"
              value={form.price}
              onChange={(e) => set("price", Number(e.target.value))}
            />
          </Field>
          <Field label="วันหมดอายุ">
            <DatePicker
              className="border-1 w-full h-[40px] border-gray-300 font-semibold rounded-md text-sm"
              showTime
              placeholder="วันหมดอายุ"
              value={form.expireAt ? dayjs(form.expireAt) : null}
              onChange={(date) => set("expireAt", date ? date.valueOf() : null)}
            />
          </Field>

          {/* Items */}
          <div className="col-span-1 sm:col-span-2 border border-gray-200 rounded-md p-3 bg-gray-50/50 mt-1">
            <div className="flex justify-between items-center mb-2">
              <p className="text-xs font-bold text-gray-700">คูปองในชุดแพ็กเกจ</p>
              <button
                type="button"
                className="text-xs font-bold bg-blue text-white px-2.5 py-1 rounded-md hover:bg-blue/90 cursor-pointer shadow-2xs"
                onClick={addItem}
              >
                + เพิ่มคูปอง
              </button>
            </div>
            <div className="space-y-2">
              {(form.items || []).map((item, index) => (
                <BundleItemRow
                  key={index}
                  index={index}
                  item={item}
                  allCoupons={allCoupons}
                  onChange={updateItem}
                  onRemove={removeItem}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-4 border-t border-gray-200 bg-gray-50/50 justify-between items-center">
        <button
          type="button"
          className="p-2.5 text-red-600 hover:bg-red-50 rounded-md font-bold text-sm flex items-center gap-1.5 cursor-pointer transition-colors"
          onClick={handleDelete}
        >
          <MdOutlineDeleteOutline className="text-lg" /> ลบชุดแพ็กเกจนี้
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md font-bold text-sm text-gray-700 cursor-pointer"
            onClick={() => setSelectedBundle(false)}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            className="px-5 py-2 bg-blue hover:bg-blue/90 rounded-md font-bold text-sm text-white cursor-pointer shadow-xs"
            onClick={handleEdit}
          >
            บันทึกการแก้ไข
          </button>
        </div>
      </div>
    </div>
  );
};

// Backward compatible export alias
export const ManageBundle = (props) => <ManageCoupon initialCategory="bundle" {...props} />;
